import { Link } from 'react-router-dom'
import Related from '../components/Related.jsx'
import FaqBlock from '../components/FaqBlock.jsx'
import { faqLd } from '../lib/faq.js'
import Seo from '../components/Seo.jsx'

const GUIDES_FAQ = [
  { q: 'Where should I start with peptide reconstitution?', a: 'Start with the dosage guide, which explains the single relationship between vial amount, bacteriostatic water, and dose — get that straight and the units to draw fall out automatically. Then use the guided mixing calculator to apply it to your own vial in plain English.' },
  { q: 'What is the difference between reconstitution and dosing?', a: 'Reconstitution is mixing the freeze-dried peptide with bacteriostatic water to make a liquid you can measure. Dosing is how much of that liquid you draw for each injection. The amount of water you add changes the units per dose but not the dose itself.' },
  { q: 'How do I know a peptide is what it claims to be?', a: 'Ask for a Certificate of Analysis (COA) — a lab report showing identity and purity from HPLC and mass spectrometry. Our COA guide explains what a legitimate one contains and the red flags of a fake or decorative report.' },
]

const GUIDES = [
  { to: '/peptide-dosage-guide', icon: '🧮', title: 'Peptide Dosage Guide', desc: 'How reconstitution works, a worked example, mcg vs mg, and the mistakes to avoid.' },
  { to: '/how-to-read-a-coa', icon: '📄', title: 'How to Read a COA', desc: 'What a Certificate of Analysis is, what HPLC and MS purity mean, and how to spot a real one.' },
  { to: '/guide', icon: '🧑‍⚕️', title: 'Guided Mixing Calculator', desc: 'A step-by-step, fill-in-the-blank tool that turns your vial and dose into plain-English instructions.' },
]

export default function Guides() {
  return (
    <main className="page">
      <Seo
        title="Peptide Reconstitution & Dosing Guides | CalculateMyPeptides"
        description="Plain-language peptide guides — how to calculate a dose, how to reconstitute a vial, and how to read a Certificate of Analysis (COA)."
        image="/og/guide.png"
        jsonLd={faqLd('Peptide guides — FAQ', GUIDES_FAQ)}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Guides', path: '/guides' }]}
      />
      <div className="container">
        <div style={{ marginBottom: 22 }}>
          <span className="pill blue" style={{ marginBottom: 8 }}>Learn</span>
          <h1 className="section-title">Peptide Guides</h1>
          <p className="section-sub" style={{ maxWidth: '60ch' }}>
            Practical, plain-language guides to the parts people actually get stuck on — dosing math, reconstitution,
            and reading a lab report. Educational only, not medical advice.
          </p>
        </div>

        <div className="pep-grid">
          {GUIDES.map((g) => (
            <Link key={g.to} to={g.to} className="card pep-card fade-in">
              <div className="pep-card-top">
                <div className="wc-icon">{g.icon}</div>
                <h2 style={{ fontSize: '1.1rem', margin: '0 0 4px' }}>{g.title}</h2>
                <p className="pep-summary">{g.desc}</p>
              </div>
              <div className="pep-tags"><span className="pep-arrow">Read →</span></div>
            </Link>
          ))}
        </div>

        <section className="page-narrow" style={{ margin: '32px auto 0' }}>
          <h2 className="pep-h2">Where to start</h2>
          <p>
            If you’re new to reconstitution, begin with the <Link to="/peptide-dosage-guide">dosage guide</Link> —
            it walks through the one formula that ties vial size, bacteriostatic water, and dose together, with a
            worked example. Once the math clicks, the <Link to="/guide">guided mixing calculator</Link> turns your
            own vial and dose into plain-English steps.
          </p>
          <p>
            Before you trust a vial at all, the <Link to="/how-to-read-a-coa">COA guide</Link> shows how to read a
            Certificate of Analysis — the lab report that tells you whether a product is what it claims and how pure
            it is. Together these cover the three things people get stuck on: the math, the mixing, and the sourcing.
            Keep the <Link to="/glossary">glossary</Link> open in another tab for any unfamiliar term.
          </p>
          <FaqBlock title="Peptide guide FAQ" items={GUIDES_FAQ} />
        </section>

        <Related
          title="Also useful"
          links={[
            { to: '/calculator', label: 'Peptide calculator', desc: 'Run your own numbers' },
            { to: '/stacks', label: 'Peptide stacks', desc: 'Common combinations' },
            { to: '/glossary', label: 'Glossary', desc: 'Every term defined' },
            { to: '/peptides', label: 'Peptide library', desc: 'Per-compound profiles' },
          ]}
        />
      </div>
    </main>
  )
}
